import React, { useState, useRef } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import filesD3Service from "../../services/filesD3Service";
import config from "../../config/environment";
import PageHeader from "../shared/PageHeader";
import { AssistantDoneBanner, useAssistantReturn } from "../Assistant/assistantReturn";
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  Upload,
  CloudUpload,
  FolderOpen,
  Globe,
  Cloud,
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  Video,
  Music,
  File as FileIcon,
  Trash2,
  Loader2,
  Clock,
  XCircle,
} from "lucide-react";
import styles from "./KnowledgeBaseD3.module.scss";
import hubStyles from "../KnowledgeBaseHub/KnowledgeBaseHub.module.scss";
import viewStyles from "../KnowledgeBaseView/KnowledgeBaseView.module.scss";

/** Route `:folderId` → API (legacy: Number; opaque non-numeric ids stay as string — never NaN). */
function folderIdForUploadPayload(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (!s) return null;
  const n = Number(s);
  if (Number.isFinite(n)) return n;
  return s;
}

/** Axios interceptor replaces failures with `Error` + `error.data`; raw axios keeps `error.response`. */
function extractUploadErrorMessage(error, fallback) {
  const d = error?.response?.data ?? error?.data;
  const detail = d?.detail ?? d?.message;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const parts = detail.map((item) => (typeof item === 'string' ? item : item?.msg)).filter(Boolean);
    if (parts.length) return parts.join(' ');
  }
  if (d && typeof d === 'object' && typeof d.error === 'string') return d.error;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return fallback;
}

function getQueuedActualFile(actualFilesMap, queueId) {
  return (
    actualFilesMap.get(queueId) ??
    actualFilesMap.get(Number(queueId)) ??
    actualFilesMap.get(String(queueId))
  );
}

const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';

/** The async-loaded Google scripts (index.html) expose these globals once ready. */
function areGoogleScriptsReady() {
  return Boolean(window.google?.accounts?.oauth2 && window.gapi);
}

/** Resolves once the Google scripts have finished loading, or rejects after a timeout. */
function waitForGoogleScripts(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    if (areGoogleScriptsReady()) {
      resolve();
      return;
    }
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (areGoogleScriptsReady()) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() - startedAt > timeoutMs) {
        clearInterval(timer);
        reject(new Error('Google Drive scripts failed to load'));
      }
    }, 150);
  });
}

const KnowledgeBaseD3 = () => {
  const { isDarkMode } = useTheme();
  const { folderId } = useParams();
  // Arrived from Claude / Town: end on "go back to your chat", not the folder.
  const assistantReturn = useAssistantReturn();
  const navigate = useNavigate();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [actualFiles, setActualFiles] = useState(new Map());
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [scrapUrl, setScrapUrl] = useState("");
  const [isScraping, setIsScraping] = useState(false);
  const [isConnectingDrive, setIsConnectingDrive] = useState(false);
  const [isImportingDrive, setIsImportingDrive] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, fileId: null });

  const fileInputRef = useRef(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleFileUpload = (event) => {
    const files = event.target.files;
    if (files.length > 0) {
      addFiles(Array.from(files));
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      addFiles(Array.from(files));
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const addFiles = (files) => {
    const now = Date.now();
    const newFiles = files.map((file, index) => ({
      id: now + index,
      name: file.name,
      size: formatFileSize(file.size),
      sizeBytes: file.size,
      type: getFileType(file.name),
      status: 'pending',
      uploadDate: new Date().toLocaleString(),
      uid: file.uid || undefined,
      file: file
    }));

    setUploadedFiles(prev => [...prev, ...newFiles]);
    
    // Store actual File objects
    setActualFiles(prev => {
      const newMap = new Map(prev);
      newFiles.forEach(newFile => {
        newMap.set(newFile.id, newFile.file);
      });
      return newMap;
    });

    showToast(`${files.length} file(s) added to upload queue`, 'success');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const typeMap = {
      'pdf': 'PDF',
      'doc': 'Word',
      'docx': 'Word',
      'txt': 'Text',
      'md': 'Markdown',
      'xls': 'Excel',
      'xlsx': 'Excel',
      'ppt': 'PowerPoint',
      'pptx': 'PowerPoint',
      'jpg': 'Image',
      'jpeg': 'Image',
      'png': 'Image',
      'gif': 'Image',
      'mp4': 'Video',
      'avi': 'Video',
      'mp3': 'Audio',
      'wav': 'Audio'
    };
    return typeMap[extension] || 'File';
  };

  const getFileIcon = (fileType) => {
    const p = { size: 22, strokeWidth: 2 };
    switch (fileType) {
      case 'PDF':
        return <FileText {...p} color="#ef4444" aria-hidden />;
      case 'Image':
        return <ImageIcon {...p} color="#16a34a" aria-hidden />;
      case 'Video':
        return <Video {...p} color="#ca8a04" aria-hidden />;
      case 'Audio':
        return <Music {...p} color="#9333ea" aria-hidden />;
      default:
        return <FileIcon {...p} color="#6b7280" aria-hidden />;
    }
  };

  const getStatusIcon = (status) => {
    const p = { size: 20, strokeWidth: 2 };
    switch (status) {
      case 'completed':
        return <CheckCircle2 {...p} color="#16a34a" aria-hidden />;
      case 'error':
        return <XCircle {...p} color="#dc2626" aria-hidden />;
      case 'uploading':
        return <Loader2 {...p} color="#ca8a04" className={viewStyles.spin} aria-hidden />;
      default:
        return <Clock {...p} color="#6b7280" aria-hidden />;
    }
  };

  const getStatusChipClass = (status) => {
    switch (status) {
      case 'completed':
        return hubStyles.folderChipOk;
      case 'error':
        return viewStyles.filesStatDangerChip;
      case 'uploading':
        return viewStyles.filesStatWarnChip;
      default:
        return hubStyles.folderChip;
    }
  };

  const handleDeleteFile = (fileId) => {
    setDeleteDialog({ open: true, fileId });
  };

  const confirmDeleteFile = () => {
    const fileId = deleteDialog.fileId;
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    setActualFiles(prev => {
      const newMap = new Map(prev);
      newMap.delete(fileId);
      return newMap;
    });
    setDeleteDialog({ open: false, fileId: null });
    showToast('File removed from upload queue', 'info');
  };

  const isScalarValue = (value) => {
    const valueType = typeof value;
    return value === null || valueType === 'string' || valueType === 'number' || valueType === 'boolean';
  };

  const buildFlatMetadataFilters = (fileId, uid, sourceFilters = {}) => {
    const flatFilters = {};

    if (sourceFilters && typeof sourceFilters === 'object' && !Array.isArray(sourceFilters)) {
      Object.entries(sourceFilters).forEach(([key, value]) => {
        if (isScalarValue(value)) {
          flatFilters[key] = value;
        }
      });
    }

    flatFilters.file_id = fileId;
    if (uid) {
      flatFilters.uid = uid;
    }

    return flatFilters;
  };

  const handleSaveToServer = async () => {
    if (uploadedFiles.length === 0) {
      showToast('No files to upload', 'warning');
      return;
    }

    setIsSaving(true);

    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Validate folder ID
      if (!folderId) {
        showToast('No folder specified for upload', 'error');
        return;
      }

      const folderIdPayload = folderIdForUploadPayload(folderId);
      if (folderIdPayload == null) {
        showToast('Invalid folder id for upload.', 'error');
        return;
      }

      // Ensure every queued file has its actual file object (Map keys may differ number vs string)
      const missingFiles = uploadedFiles.filter((file) => !getQueuedActualFile(actualFiles, file.id));
      if (missingFiles.length > 0) {
        showToast('Some files are missing from upload queue. Please re-add and try again.', 'error');
        return;
      }

      // Enforce unique file_name values in request metadata
      const fileNames = uploadedFiles.map(
        (file) => getQueuedActualFile(actualFiles, file.id)?.name || file.name
      );
      const uniqueFileNames = new Set(fileNames);
      if (uniqueFileNames.size !== fileNames.length) {
        showToast('Duplicate file_name detected. Keep only one file per name.', 'error');
        return;
      }

      // Prepare request metadata in JSON format (file_size must be bytes — not formatted label)
      const requestMetadata = {
        request_type: "d3_knowledge_base_upload",
        additional_data: {
          folder_id: folderIdPayload,
          files_metadata: uploadedFiles.map((file, index) => {
            const actualFile = getQueuedActualFile(actualFiles, file.id);
            const parsedFileId = Number(file.id);
            const fileId = Number.isFinite(parsedFileId) ? Math.trunc(parsedFileId) : (Date.now() + index);
            const uid = file.uid ? String(file.uid) : undefined;
            const sourceFilters = file.metadata_filters || file.metadataFilters || {};
            const metadataFilters = buildFlatMetadataFilters(fileId, uid, sourceFilters);
            const byteSize =
              typeof actualFile?.size === 'number'
                ? actualFile.size
                : typeof file.sizeBytes === 'number'
                  ? file.sizeBytes
                  : undefined;

            return {
              file_id: fileId,
              file_name: actualFile.name,
              ...(uid ? { uid } : {}),
              ...(byteSize != null ? { file_size: byteSize } : {}),
              ...(Object.keys(metadataFilters).length > 0 ? { metadata_filters: metadataFilters } : {}),
            };
          })
        }
      };

      // Add request_json field
      formData.append('request_json', JSON.stringify(requestMetadata));

      // Add actual File objects to FormData
      uploadedFiles.forEach((file) => {
        const actualFile = getQueuedActualFile(actualFiles, file.id);
        if (actualFile) {
          formData.append('files', actualFile);
        }
      });

      console.log('Sending FormData to server:', {
        request_json: requestMetadata,
        files_count: uploadedFiles.length,
        formData_entries: Array.from(formData.entries()).map(([key, value]) => ({
          key,
          value: value instanceof File ? `File: ${value.name}` : value
        }))
      });

      // Make API call
      const result = await filesD3Service.uploadFile(formData);
      console.log('Server response:', result);

      // Update file statuses
      setUploadedFiles(prev => prev.map(file => ({ ...file, status: 'completed' })));

      if (assistantReturn.fromAssistant) {
        assistantReturn.markDone();
        showToast(`Successfully uploaded ${uploadedFiles.length} file(s)!`, 'success');
      } else {
        showToast(`Successfully uploaded ${uploadedFiles.length} file(s)! Redirecting to folder view...`, 'success');

        // Redirect back to folder files view after successful upload
        setTimeout(() => {
          navigate(`/knowledge-base-d3/folder/${folderId}`);
        }, 2000);
      }

    } catch (error) {
      console.error('Error uploading files to server:', error);
      setUploadedFiles(prev => prev.map(file => ({ ...file, status: 'error' })));
      showToast(
        extractUploadErrorMessage(error, 'Failed to upload files to server. Please try again.'),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleScrapFromUrl = async () => {
    const rawUrl = scrapUrl.trim();
    if (!rawUrl) {
      showToast('Please enter a URL to scrap', 'warning');
      return;
    }

    if (!folderId) {
      showToast('No folder specified for URL scrap', 'error');
      return;
    }

    const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;

    let parsedUrl;
    try {
      parsedUrl = new URL(normalizedUrl);
    } catch (error) {
      showToast('Please enter a valid URL', 'error');
      return;
    }

    const fileId = Date.now();
    const hostName = parsedUrl.hostname || normalizedUrl;

    const folderIdPayload = folderIdForUploadPayload(folderId);
    if (folderIdPayload == null) {
      showToast('Invalid folder id for URL upload.', 'error');
      return;
    }

    const requestPayload = {
      request_type: "d3_web_upload",
      additional_data: {
        folder_id: folderIdPayload,
        url: normalizedUrl,
        files_metadata: [
          {
            file_id: fileId,
            file_name: hostName,
            metadata_filters: {
              file_id: fileId,
            },
            uid: `web-${hostName}`,
          },
        ],
      },
    };

    setIsScraping(true);
    try {
      const result = await filesD3Service.uploadWebUrl(requestPayload);
      console.log('Web scrap upload response:', result);
      setScrapUrl('');
      if (assistantReturn.fromAssistant) {
        assistantReturn.markDone();
        showToast('URL submitted successfully.', 'success');
      } else {
        showToast('URL submitted successfully. Redirecting to folder view...', 'success');
        setTimeout(() => {
          navigate(`/knowledge-base-d3/folder/${folderId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Error uploading URL for scrap:', error);
      if (error?.isTimeout) {
        showToast('Scraping is taking longer than expected. Please wait and check folder status shortly.', 'warning');
      } else {
        showToast('Failed to submit URL for scrap. Please try again.', 'error');
      }
    } finally {
      setIsScraping(false);
    }
  };

  // POST the picked Drive files to the backend, which imports them asynchronously.
  const importGoogleDriveFiles = async (docs, accessToken) => {
    const folderIdPayload = folderIdForUploadPayload(folderId);
    if (folderIdPayload == null) {
      showToast('Invalid folder id for Google Drive import.', 'error');
      return;
    }

    const baseId = Date.now();
    const files = docs.map((doc, index) => ({
      drive_file_id: doc.id,
      // Numeric and unique per file.
      file_id: baseId + index * 1000 + Math.floor(Math.random() * 1000),
      file_name: doc.name,
      // `uid` is intentionally omitted — the backend rejects a blank uid; file_id is sufficient.
      file_size: doc.sizeBytes || 0,
      metadata_filters: {},
    }));

    setIsImportingDrive(true);
    try {
      await filesD3Service.importGoogleDrive({
        folder_id: folderIdPayload,
        access_token: accessToken,
        files,
      });
      if (assistantReturn.fromAssistant) {
        assistantReturn.markDone();
        showToast(`Importing ${files.length} file(s) from Google Drive.`, 'success');
      } else {
        showToast(
          `Importing ${files.length} file(s) from Google Drive. Redirecting to folder view...`,
          'success'
        );
        setTimeout(() => {
          navigate(`/knowledge-base-d3/folder/${folderId}`);
        }, 1800);
      }
    } catch (error) {
      console.error('Error importing Google Drive files:', error);
      const message = extractUploadErrorMessage(error, 'Failed to import files from Google Drive.');
      if (error?.isTimeout) {
        // Request timed out, but the import is processed asynchronously — it likely still went through.
        showToast(
          'Import is taking longer than expected. Your files may still be processing — check the folder shortly.',
          'warning'
        );
        if (!assistantReturn.fromAssistant) {
          setTimeout(() => {
            navigate(`/knowledge-base-d3/folder/${folderId}`);
          }, 2200);
        }
      } else if (error?.status === 400 && /token.*(invalid|expired)/i.test(message)) {
        // Short-lived token expired between picking and submitting — user must reconnect.
        showToast('Google session expired. Please connect Google Drive again.', 'warning');
      } else {
        showToast(message, 'error');
      }
    } finally {
      setIsImportingDrive(false);
    }
  };

  // Open the Google Picker so the user can select Drive files to import.
  const openGooglePicker = (accessToken) => {
    window.gapi.load('picker', () => {
      const view = new window.google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false);
      const picker = new window.google.picker.PickerBuilder()
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(accessToken)
        .setDeveloperKey(config.GOOGLE_API_KEY)
        .addView(view)
        .setCallback((data) => {
          if (data.action !== window.google.picker.Action.PICKED) return;
          if (Array.isArray(data.docs) && data.docs.length > 0) {
            importGoogleDriveFiles(data.docs, accessToken);
          }
        })
        .build();
      picker.setVisible(true);
    });
  };

  // Connect → fetch a fresh access token right before opening the Picker (token is ~1h).
  const handleConnectGoogleDrive = async () => {
    if (!config.GOOGLE_CLIENT_ID || !config.GOOGLE_API_KEY) {
      showToast(
        'Google Drive is not configured. Set REACT_APP_GOOGLE_CLIENT_ID and REACT_APP_GOOGLE_API_KEY.',
        'error'
      );
      return;
    }

    setIsConnectingDrive(true);
    try {
      await waitForGoogleScripts();
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: config.GOOGLE_CLIENT_ID,
        scope: GOOGLE_DRIVE_SCOPE,
        callback: (resp) => {
          setIsConnectingDrive(false);
          if (resp?.error || !resp?.access_token) {
            showToast('Google Drive authorization was cancelled or failed.', 'warning');
            return;
          }
          openGooglePicker(resp.access_token);
        },
        error_callback: () => {
          setIsConnectingDrive(false);
          showToast('Google Drive authorization was cancelled.', 'warning');
        },
      });
      tokenClient.requestAccessToken();
    } catch (error) {
      console.error('Error connecting to Google Drive:', error);
      setIsConnectingDrive(false);
      showToast('Could not load Google Drive. Check your connection and try again.', 'error');
    }
  };

  const showToast = (message, severity = 'info') => {
    setToast({ open: true, message, severity });
  };

  const handleToastClose = () => {
    setToast({ open: false, message: '', severity: 'info' });
  };

  const renderUploadTab = () => (
    <Box className={styles.uploadTab}>
      <Box
        className={`${styles.uploadArea} ${isDragOver ? styles.dragOver : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.md,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.mp4,.avi,.mp3,.wav"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />

        <CloudUpload size={48} strokeWidth={1.5} style={{ color: 'var(--text-secondary)', marginBottom: 16 }} aria-hidden />
        <Typography variant="h5" className={styles.uploadText}>
          {isDragOver ? 'Drop files here!' : 'Upload Files'}
        </Typography>
        <Typography variant="body1" className={styles.uploadSubtext}>
          {isDragOver ? 'Release to upload' : 'Drag and drop files here, or click to browse'}
        </Typography>

        <Typography variant="caption" className={styles.supportedFormats}>
          Supported formats: PDF, DOC, DOCX, TXT, MD, XLS, XLSX, PPT, PPTX, Images, Videos, Audio
        </Typography>
      </Box>
    </Box>
  );

  const renderGoogleDriveTab = () => {
    const driveBusy = isConnectingDrive || isImportingDrive;
    const driveLabel = isConnectingDrive
      ? 'Connecting...'
      : isImportingDrive
        ? 'Importing...'
        : 'Connect Google Drive';

    return (
      <Box className={styles.comingSoonTab}>
        <Cloud size={48} strokeWidth={1.25} style={{ color: 'var(--text-secondary)', marginBottom: 16 }} aria-hidden />
        <Typography variant="h6" className={styles.comingSoonTitle}>
          Google Drive Integration
        </Typography>
        <Typography variant="body2" className={styles.comingSoonSubtitle}>
          Connect your Google Drive and pick files to import into this folder.
        </Typography>
        <button
          type="button"
          disabled={driveBusy}
          className={hubStyles.uploadBtn}
          onClick={handleConnectGoogleDrive}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <Cloud size={18} strokeWidth={2} aria-hidden />
          {driveLabel}
        </button>

        {driveBusy && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mt: 2,
              maxWidth: 380,
              mx: 'auto',
            }}
          >
            <Loader2
              size={16}
              strokeWidth={2}
              className={viewStyles.spin}
              style={{ flexShrink: 0, marginTop: 3 }}
              aria-hidden
            />
            <Typography
              variant="body2"
              sx={{ textAlign: 'left', color: 'var(--text-secondary)' }}
            >
              {isConnectingDrive
                ? 'Waiting for Google authorization — complete the sign-in popup to continue.'
                : 'Importing your files from Google Drive. Please stay on this page — you’ll be redirected to the folder automatically when it’s done.'}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderDropboxTab = () => (
    <Box className={styles.comingSoonTab}>
      <Cloud size={48} strokeWidth={1.25} style={{ color: 'var(--text-secondary)', marginBottom: 16 }} aria-hidden />
      <Typography variant="h6" className={styles.comingSoonTitle}>
        Dropbox Integration
      </Typography>
      <Typography variant="body2" className={styles.comingSoonSubtitle}>
        Coming Soon - Connect your Dropbox to easily import files
      </Typography>
      <button
        type="button"
        disabled
        className={`${hubStyles.viewAgentBtn} ${styles.comingSoonButton}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <Cloud size={18} strokeWidth={2} aria-hidden />
        Connect Dropbox
      </button>
    </Box>
  );

  const renderWebScrapTab = () => (
    <Box className={styles.scrapTab}>
      <Typography variant="h6" className={styles.scrapTitle}>
        Scrap from URL
      </Typography>
      <Typography variant="body2" className={styles.scrapSubtitle}>
        Enter a public URL and click Scrap to upload web content into this folder.
      </Typography>

      <Box className={styles.scrapForm}>
        <TextField
          fullWidth
          placeholder="https://www.example.com"
          value={scrapUrl}
          onChange={(event) => setScrapUrl(event.target.value)}
          disabled={isScraping}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleScrapFromUrl();
            }
          }}
          className={styles.scrapInput}
        />
        <button
          type="button"
          onClick={handleScrapFromUrl}
          disabled={isScraping || !scrapUrl.trim()}
          className={`${hubStyles.uploadBtn} ${styles.scrapButton}`}
        >
          {isScraping ? 'Scraping...' : 'Scrap'}
        </button>
      </Box>
    </Box>
  );

  const tabBtnClass = (idx) =>
    `${hubStyles.tabBtn} ${activeTab === idx ? hubStyles.tabBtnActive : ''}`;

  if (!folderId) {
    return <Navigate to="/knowledge-hub?tab=d3" replace />;
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.knowledgeBase}>
        <PageHeader
          title="Upload Files to Customer Data"
          subtitle="Add files to your customer data folder"
          backButton={<FolderOpen size={22} strokeWidth={2} />}
          onBackClick={() => navigate(`/knowledge-base-d3/folder/${folderId}`)}
          actionButton={
            uploadedFiles.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={hubStyles.viewAgentBtn}
                  onClick={() => navigate(`/knowledge-base-d3/folder/${folderId}`)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <FolderOpen size={18} strokeWidth={2} aria-hidden />
                  Back to Folder
                </button>
                <button
                  type="button"
                  className={hubStyles.uploadBtn}
                  onClick={handleSaveToServer}
                  disabled={isSaving || uploadedFiles.length === 0}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <Upload size={18} strokeWidth={2} aria-hidden />
                  {isSaving ? 'Uploading...' : `Upload ${uploadedFiles.length} File(s)`}
                </button>
              </Box>
            )
          }
        />

        <Box className={styles.content}>
          {assistantReturn.done && <AssistantDoneBanner sx={{ mb: 2 }} />}
          <div className={`${hubStyles.listCard} ${styles.uploadSection}`}>
            <div
              className={hubStyles.tabGroup}
              style={{
                margin: 12,
                width: 'calc(100% - 24px)',
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                className={tabBtnClass(0)}
                onClick={() => handleTabChange(null, 0)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <CloudUpload size={18} strokeWidth={2} aria-hidden />
                Local Files
              </button>
              <button
                type="button"
                className={tabBtnClass(1)}
                onClick={() => handleTabChange(null, 1)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <Globe size={18} strokeWidth={2} aria-hidden />
                Web Scrap
              </button>
              <button
                type="button"
                className={tabBtnClass(2)}
                onClick={() => handleTabChange(null, 2)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <CheckCircle2 size={18} strokeWidth={2} aria-hidden />
                Google Drive
              </button>
              <button
                type="button"
                className={tabBtnClass(3)}
                onClick={() => handleTabChange(null, 3)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <Cloud size={18} strokeWidth={2} aria-hidden />
                Dropbox
              </button>
            </div>

            <Box className={styles.tabContent}>
              {activeTab === 0 && renderUploadTab()}
              {activeTab === 1 && renderWebScrapTab()}
              {activeTab === 2 && renderGoogleDriveTab()}
              {activeTab === 3 && renderDropboxTab()}
            </Box>
          </div>

          {uploadedFiles.length > 0 && (
            <div className={`${hubStyles.listCard} ${styles.filesSection}`}>
              <div className={hubStyles.listHeader}>
                <div className={hubStyles.listHeaderLeft}>
                  <p className={hubStyles.listTitle}>Files to upload</p>
                  <span className={hubStyles.countLabel}>{uploadedFiles.length} files</span>
                </div>
              </div>
              {uploadedFiles.map((file) => (
                <div key={file.id} className={hubStyles.fileRow} style={{ cursor: 'default' }}>
                  <div className={hubStyles.fileRowMain}>
                    <div className={`${hubStyles.typeIconWrap} ${hubStyles.typeDoc}`}>
                      {getFileIcon(file.type)}
                    </div>
                    <div className={hubStyles.fileMeta}>
                      <h3>{file.name}</h3>
                      <p className="desc">{file.size}</p>
                      <div className="subRow">
                        <span className={`${hubStyles.folderChip} ${hubStyles.folderChipAccent}`}>{file.type}</span>
                        <span>·</span>
                        <span>{file.uploadDate}</span>
                      </div>
                    </div>
                  </div>
                  <div className={viewStyles.fileRowBlock}>
                    <div className={viewStyles.fileRowMetaLine}>
                      {getStatusIcon(file.status)}
                      <span className={`${hubStyles.folderChip} ${getStatusChipClass(file.status)}`}>
                        {file.status}
                      </span>
                    </div>
                  </div>
                  <div className={`${hubStyles.rowActions} ${viewStyles.fileFolderRowActions}`}>
                    <Tooltip title="Remove from queue">
                      <button
                        type="button"
                        className={`${hubStyles.iconBtn} ${viewStyles.fileDeleteBtn}`}
                        onClick={() => handleDeleteFile(file.id)}
                        aria-label="Remove from queue"
                      >
                        <Trash2 size={18} strokeWidth={2} />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, fileId: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Remove File</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Are you sure you want to remove this file from the upload queue?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, fileId: null })}>
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteFile} 
              color="error" 
              variant="contained"
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast Notifications */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={handleToastClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleToastClose}
            severity={toast.severity}
            sx={{ width: '100%' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default KnowledgeBaseD3;
